"use client"

import { Deferred, Head, usePage } from "@inertiajs/react"
import {
  CheckCircle,
  Edit05,
  LayersThree01,
  MusicNote01,
  Plus,
} from "@untitledui/icons"

import { StatCard } from "@/components/dashboard/stat-card"
import { PlaylistCard } from "@/components/playlists"
import { Button } from "@/components/ui/button"
import { EmptyState } from "@/components/ui/empty-state"
import { Link } from "@/components/ui/link"
import { DashboardLayout } from "@/layouts/dashboard-layout"
import { newPlaylistPath, ownedPlaylistsPath } from "@/routes"
import type { Playlist, SharedData } from "@/types"
import type { WarmStartLedgerRow } from "@/types"

import {
  AdminBand,
  AdminBandSkeleton,
  type AdminMetrics,
} from "./_components/admin-band"

interface MemberStats {
  playlists: number
  published: number
  drafts: number
  tracks: number
}

interface DashboardProps {
  stats: MemberStats
  playlists: Playlist[]
  drafts: Playlist[]
  // Admin-only, deferred — absent entirely for non-admins.
  admin_metrics?: AdminMetrics
  recent_seeds?: WarmStartLedgerRow[]
}

const CARD_GRID =
  "grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"

export default function Dashboard({
  stats,
  playlists,
  drafts,
  admin_metrics,
  recent_seeds,
}: DashboardProps) {
  const { visitor } = usePage<SharedData>().props
  const firstName = visitor.display_name?.split(" ")[0]
  const hasAnyPlaylists = stats.playlists > 0

  return (
    <DashboardLayout>
      <Head title="Dashboard" />
      <div className="max-w-container mx-auto flex flex-col gap-8 px-4 pt-8 pb-12 lg:px-8">
        <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="flex flex-col gap-1">
            <span
              aria-hidden="true"
              className="scotch-rule scotch-rule-action mb-1"
            />
            <h1 className="text-primary font-display text-display-xs md:text-display-sm font-semibold text-balance">
              {firstName ? `Welcome back, ${firstName}` : "Your desk"}
            </h1>
            <p className="text-md text-tertiary">
              Pick up where you left off, or start something new.
            </p>
          </div>
          <Button href={newPlaylistPath()} size="md" iconLeading={Plus}>
            New playlist
          </Button>
        </header>

        {hasAnyPlaylists ? (
          <>
            <section className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <StatCard
                icon={MusicNote01}
                color="brand"
                label="Playlists"
                value={stats.playlists}
                href={ownedPlaylistsPath()}
              />
              <StatCard
                icon={CheckCircle}
                color="success"
                label="Published"
                value={stats.published}
                href={ownedPlaylistsPath()}
              />
              <StatCard
                icon={Edit05}
                label="Drafts"
                value={stats.drafts}
                href={ownedPlaylistsPath()}
              />
              <StatCard
                icon={LayersThree01}
                label="Tracks"
                value={stats.tracks}
              />
            </section>

            {drafts.length > 0 && (
              <section className="flex flex-col gap-4">
                <div className="flex flex-col gap-0.5">
                  <h2 className="text-primary text-lg font-semibold">
                    Continue editing
                  </h2>
                  <p className="text-tertiary text-sm">
                    Drafts waiting on your finishing touch.
                  </p>
                </div>
                <div className={CARD_GRID}>
                  {drafts.map((playlist) => (
                    <PlaylistCard key={playlist.id} playlist={playlist} />
                  ))}
                </div>
              </section>
            )}

            <section className="flex flex-col gap-4">
              <div className="flex items-center justify-between gap-4">
                <h2 className="text-primary text-lg font-semibold">
                  Your playlists
                </h2>
                <Link
                  href={ownedPlaylistsPath()}
                  className="text-sm font-semibold"
                >
                  View all
                </Link>
              </div>
              {playlists.length > 0 ? (
                <div className={CARD_GRID}>
                  {playlists.map((playlist) => (
                    <PlaylistCard key={playlist.id} playlist={playlist} />
                  ))}
                </div>
              ) : (
                <p className="text-tertiary border-secondary rounded-xl border border-dashed p-6 text-center text-sm">
                  Nothing published yet. Finish a draft above to share it.
                </p>
              )}
            </section>
          </>
        ) : (
          <EmptyState size="md" className="py-12">
            <EmptyState.Header pattern="circle">
              <EmptyState.FeaturedIcon
                color="brand"
                theme="light"
                icon={MusicNote01}
              />
            </EmptyState.Header>
            <EmptyState.Content>
              <p className="text-primary text-md text-center font-semibold">
                Make your first playlist
              </p>
              <EmptyState.Description>
                Gather the tracks that matter to you. Publish it and your name
                joins the community.
              </EmptyState.Description>
            </EmptyState.Content>
            <EmptyState.Footer>
              <Button href={newPlaylistPath()} size="md" iconLeading={Plus}>
                New playlist
              </Button>
            </EmptyState.Footer>
          </EmptyState>
        )}

        {visitor.admin && (
          <Deferred
            data={["admin_metrics", "recent_seeds"]}
            fallback={<AdminBandSkeleton />}
          >
            {admin_metrics && (
              <AdminBand metrics={admin_metrics} seeds={recent_seeds ?? []} />
            )}
          </Deferred>
        )}
      </div>
    </DashboardLayout>
  )
}
